/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { SortDirection } from "~/components";
import { isNil, Maybe, sort } from "~/tools";
import { SortFilter } from "./constants";
import { MappedOrgUnit, SpendTreeItem } from "./OrganizationUnit/Lists";

type CostKeys = "currentMonth" | "forecast" | "previousMonth";

type Cost = Maybe<{
  [key in CostKeys]?: Maybe<string>;
}>;

type OrgUnit = {
  name: string;
  parentNid?: Maybe<string>;
  nid: string;
  budgetYearly: {
    budgetByMonth: string[];
  };
  cost?: Cost;
  type?: Maybe<{
    name: string;
    id: string;
  }>;
  contact?: Maybe<{
    name: string;
    id: string;
  }>;
  environments?: Maybe<{
    count?: Maybe<number>;
  }>;
};

export function mapOrgUnits(orgUnits: OrgUnit[], currentMonth: number) {
  return orgUnits.map((unit) => ({
    name: unit.name,
    parentNid: unit.parentNid ?? null,
    nid: unit.nid,
    monthlyBudget: unit.budgetYearly.budgetByMonth[currentMonth]
      ? parseFloat(unit.budgetYearly.budgetByMonth[currentMonth])
      : null,
    cost: parseFloat(unit.cost?.currentMonth ?? "0"),
    previousCost: parseFloat(unit.cost?.previousMonth ?? "0"),
    forecast: parseFloat(unit.cost?.forecast ?? "0"),
    unitType: unit?.type?.name ?? "-",
    unitId: unit?.type?.id ?? "-",
    contactPerson: unit?.contact?.name ?? "-",
    contactPersonId: unit?.contact?.id ?? "",
    environments: unit?.environments?.count ?? 0,
    subRows: [],
  }));
}

export function sortOrgUnits(
  orgUnits: SpendTreeItem[],
  filter: SortFilter,
  sortDirection: SortDirection
): SpendTreeItem[] {
  const internallySortedOrgUnits = orgUnits.map((orgUnit) => ({
    ...orgUnit,
    subRows: sortOrgUnits(orgUnit.subRows, filter, sortDirection),
  }));
  return sortByField(internallySortedOrgUnits, filter, sortDirection);
}

function sortByField(
  orgUnits: SpendTreeItem[],
  filter: SortFilter,
  sortDirection: SortDirection
): SpendTreeItem[] {
  switch (filter) {
    case SortFilter.UnitName:
      return sort(orgUnits, (a, b) =>
        sortDirection === SortDirection.Ascending
          ? b.name.localeCompare(a.name)
          : a.name.localeCompare(b.name)
      );
    case SortFilter.MonthlyCosts:
      return sort(orgUnits, (a, b) =>
        sortWithDirection(a.cost, b.cost, sortDirection)
      );
    case SortFilter.MonthlyForecastCosts:
      return sort(orgUnits, (a, b) =>
        sortWithDirection(a.forecast, b.forecast, sortDirection)
      );
    case SortFilter.PreviousMonthCosts:
      return sort(orgUnits, (a, b) =>
        sortWithDirection(a.previousCost, b.previousCost, sortDirection)
      );
    case SortFilter.MonthlyBudget:
      return sort(orgUnits, (a, b) =>
        sortWithDirection(
          a.monthlyBudget ?? 0,
          b.monthlyBudget ?? 0,
          sortDirection
        )
      );
    case SortFilter.Environments:
      return sort(orgUnits, (a, b) =>
        sortWithDirection(
          a.accumulatedEnvironments,
          b.accumulatedEnvironments,
          sortDirection
        )
      );
    default:
      throw new Error("Unsupported filter.");
  }
}

const sortWithDirection = (
  a: number,
  b: number,
  sortDirection: SortDirection
) => (sortDirection === SortDirection.Ascending ? b - a : a - b);

const flatDataToTree = (
  items: MappedOrgUnit[],
  nid: string | null = null
): MappedOrgUnit[] => {
  return items
    .filter((item) => item.parentNid === nid)
    .map((item) => ({
      ...item,
      subRows: flatDataToTree(items, item.nid),
    }));
};

export function getOrgUnitWithItsParents<
  T extends { parentNid?: Maybe<string>; nid: string }
>(item: T, allItems: T[]): T[] {
  if (isNil(item.parentNid)) {
    return [item];
  }

  const parent = allItems.find((orgUnit) => orgUnit.nid === item.parentNid);
  return isNil(parent)
    ? [item]
    : [item, ...getOrgUnitWithItsParents(parent, allItems)];
}

export function buildDataTree(data: MappedOrgUnit[]) {
  return sumBranchSpends(flatDataToTree(data));
}

const reduceSubRowsByField =
  (
    field:
      | "accumulatedEnvironments"
      | "monthlyBudget"
      | "parentCost"
      | "parentForecast"
      | "parentPreviousCost"
  ) =>
  (acc: number, item: SpendTreeItem) =>
    acc + (item[field] ?? 0);

const sumBranchSpends = (items: MappedOrgUnit[]): SpendTreeItem[] => {
  return items.map((row: MappedOrgUnit) => {
    const subRows: SpendTreeItem[] = sumBranchSpends(row.subRows);

    return {
      ...row,
      childrenBudget: subRows.reduce(reduceSubRowsByField("monthlyBudget"), 0),
      parentCost:
        row.cost - subRows.reduce(reduceSubRowsByField("parentCost"), 0),
      parentPreviousCost:
        row.previousCost -
        subRows.reduce(reduceSubRowsByField("parentPreviousCost"), 0),
      parentForecast:
        row.forecast -
        subRows.reduce(reduceSubRowsByField("parentForecast"), 0),
      accumulatedEnvironments:
        Number(row.environments) +
        subRows.reduce(reduceSubRowsByField("accumulatedEnvironments"), 0),
      subRows: subRows ?? [],
    };
  });
};
